import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshControl, ScrollView, TouchableOpacity, View } from 'react-native';

import { useUserContext } from '../../../common/utils/context';
import { MY_DAY_STEP_STATUS } from '../../../common/utils/myDay/driverDaySteps';
import { formatDate } from '../../../common/utils/utils';

import {
	Button,
	Dialog,
	Heading,
	Spinner,
	Text,
} from '../../components';
import withMediaPicker from '../../hocs/withMediaPicker';
import { useRefreshPage } from '../../hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { ManualRestockDialog } from '../manualRestock';
import ServiceRunVehicleDetail from '../servicerun/ServiceRunDetail/ServiceRunVehicleDetail';
import ServiceRunBrief from '../servicerun/ServiceRunBrief';
import useDriverDayActions from '../driver/useDriverDayActions';
import useDriverDayModel from '../driver/useDriverDayModel';
import DriverMyDayFocusCard, { FOCUS_KEY_CURRENT_STOP, FOCUS_KEY_START_RUN } from './DriverMyDayFocusCard';
import DriverMyDayHeader from './DriverMyDayHeader';
import { createDriverMyDayViewStyles } from './DriverMyDayView.styles';
import TrainingModeBanner from './TrainingModeBanner';
import FreshFoodList from './board/FreshFoodList';
import NextStopReasonDialog from './board/NextStopReasonDialog';
import PhaseSection from './board/PhaseSection';
import StepRow from './board/StepRow';
import StopsList from './board/StopsList';

// Classic My day board, kept for users with the legacyMobileUi fallback. Its
// data, step lists and actions come from the shared driver day hooks.
const DriverMyDayView = ({
	captureMedia,
	capturedMediaBatch,
	refetchModeData,
	onViewEmployeeDay,
	onShowPickingWork,
	trainingContext,
	onExitTraining,
	onChooseEmployee,
}) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createDriverMyDayViewStyles);
	const { theme } = useTheme();
	const {
		user,
		tenant,
		tenantSettings,
	} = useUserContext();
	const scrollViewRef = useRef(null);

	const [isBriefOpen, setIsBriefOpen] = useState(false);
	const [isVehicleDialogOpen, setIsVehicleDialogOpen] = useState(false);
	const [isNextStopDialogOpen, setIsNextStopDialogOpen] = useState(false);
	// null = follow the next action; a step key = the driver tapped a step to revisit it.
	const [focusOverrideKey, setFocusOverrideKey] = useState(null);
	// null = follow the phase defaults; true/false = user override per list.
	const [startListExpandedOverride, setStartListExpandedOverride] = useState(null);
	const [stopsListExpandedOverride, setStopsListExpandedOverride] = useState(null);
	const [endListExpandedOverride, setEndListExpandedOverride] = useState(null);
	const [freshFoodExpandedOverride, setFreshFoodExpandedOverride] = useState(null);
	const clearFocusOverride = useCallback(() => setFocusOverrideKey(null), []);

	const day = useDriverDayModel({ trainingContext });
	const {
		todaysRuns,
		setActiveRunId,
		upcomingRun,
		clockStatus,
		machineFill,
		rosterShift,
		isDocketDone,
		todayDateKey,
		isLoading,
		refetchAll,
		refetchRuns,
		serviceRun,
		isTraining,
		isOnline,
		trainingOffline,
		setSelectedDocketId,
		sortedDockets,
		hasEligiblePickListItems,
		nextIncompleteDocket,
		activeDocket,
		reorderableStops,
		startOfDaySteps,
		endOfDaySteps,
		visibleStartSteps,
		visibleEndSteps,
		runStarted,
		nextAction,
		activePhaseKey,
		startOfDayComplete,
		isClockedOutForDay,
		freshFoodStops,
	} = day;
	const actions = useDriverDayActions(day, {
		stopRoute: 'DocketDetailView',
		onStepConfirmed: clearFocusOverride,
		captureMedia,
		capturedMediaBatch,
	});
	const {
		requireTrainingConnection,
		openStopDetails,
		openPickList,
		openPickListSummary,
		submitNextStops: saveNextStops,
		isMakingNextStop,
		manualRestock,
	} = actions;

	const refetchMyDay = useCallback(() => {
		const requests = [refetchAll()];
		if (!isTraining && refetchModeData) {
			requests.push(refetchModeData());
		}

		return Promise.all(requests);
	}, [isTraining, refetchAll, refetchModeData]);
	const [refreshPage, isRefreshing] = useRefreshPage(refetchMyDay);

	const selectStopInMyDay = useCallback((docket) => {
		if (!docket) {
			return;
		}
		if (isDocketDone(docket)) {
			openStopDetails(docket);
			return;
		}

		setSelectedDocketId(docket.id);
		setFocusOverrideKey(FOCUS_KEY_CURRENT_STOP);
		scrollViewRef.current?.scrollTo({ y: 0, animated: true });
	}, [isDocketDone, openStopDetails, setSelectedDocketId]);

	const getFreshFoodStopDocket = useCallback(stop => sortedDockets.find(docket => docket.id === stop.serviceDocketId), [sortedDockets]);
	const openFreshFoodStopPickList = useCallback((stop) => {
		openPickList(getFreshFoodStopDocket(stop));
	}, [getFreshFoodStopDocket, openPickList]);
	// Same conditions as openPickList, so a stop it would ignore shows no chevron and is not tappable.
	const canOpenFreshFoodStop = useCallback(stop => Boolean(serviceRun?.planned && getFreshFoodStopDocket(stop)?.pickList?.id), [getFreshFoodStopDocket, serviceRun?.planned]);

	const openBrief = useCallback(() => {
		setIsBriefOpen(true);
	}, []);

	const submitNextStops = useCallback(async (serviceDocketIds, reason) => {
		if (await saveNextStops(serviceDocketIds, reason)) {
			setIsNextStopDialogOpen(false);
		}
	}, [saveNextStops]);
	const openNextStopDialog = useCallback(() => {
		if (requireTrainingConnection()) {
			setIsNextStopDialogOpen(true);
		}
	}, [requireTrainingConnection]);

	const showStopsSection = runStarted || startOfDayComplete;
	const showEndOfDaySection = runStarted;

	// Progressive disclosure: the focused step is the next action unless the
	// driver taps another step (e.g. reopening the brief) to revisit it.
	const defaultFocusKey = useMemo(() => {
		if (!nextAction) {
			return null;
		}

		if (nextAction.type === 'stop') {
			return FOCUS_KEY_CURRENT_STOP;
		}

		if (nextAction.type === 'startRun') {
			return FOCUS_KEY_START_RUN;
		}

		return nextAction.step.key;
	}, [nextAction]);

	const focusableKeys = useMemo(() => {
		const keys = new Set();
		visibleStartSteps
			.filter(step => step.status !== MY_DAY_STEP_STATUS.BLOCKED)
			.forEach(step => keys.add(step.key));
		if (showStopsSection && activeDocket) {
			keys.add(FOCUS_KEY_CURRENT_STOP);
		}
		if (showEndOfDaySection) {
			visibleEndSteps
				.filter(step => step.status !== MY_DAY_STEP_STATUS.BLOCKED)
				.forEach(step => keys.add(step.key));
		}
		return keys;
	}, [activeDocket, showEndOfDaySection, showStopsSection, visibleEndSteps, visibleStartSteps]);

	const focusKey = focusOverrideKey && focusableKeys.has(focusOverrideKey)
		? focusOverrideKey
		: defaultFocusKey;
	const isFocusOverridden = Boolean(focusKey) && focusKey !== defaultFocusKey;

	// A new run resets the driver's manual focus/list overrides.
	useEffect(() => {
		setFocusOverrideKey(null);
		setStartListExpandedOverride(null);
		setStopsListExpandedOverride(null);
		setEndListExpandedOverride(null);
		setFreshFoodExpandedOverride(null);
	}, [serviceRun?.id]);

	// Entering a new phase clears manual expand/collapse overrides so the
	// accordion snaps back to "only the active phase is open".
	useEffect(() => {
		setStartListExpandedOverride(null);
		setStopsListExpandedOverride(null);
		setEndListExpandedOverride(null);
		setFreshFoodExpandedOverride(null);
	}, [activePhaseKey]);

	const openVehicleDialog = useCallback(() => {
		if (requireTrainingConnection()) {
			setIsVehicleDialogOpen(true);
		}
	}, [requireTrainingConnection]);
	const onVehicleUpdated = useCallback(() => {
		setFocusOverrideKey(null);
		refetchRuns();
	}, [refetchRuns]);

	const onStepRowPress = useCallback((step) => {
		if (step.trainingUnavailable) {
			return;
		}

		setFocusOverrideKey(step.key);
		// The focus card lives at the top of the scroll view; without this a tap
		// on a lower row updates a card that is off-screen and reads as a no-op.
		scrollViewRef.current?.scrollTo({ y: 0, animated: true });

		// The vehicle step is an action, not an opening step: go straight to the dialog.
		if (step.key === 'vehicle') {
			openVehicleDialog();
		}
		else if (step.key === 'brief') {
			openBrief();
		}
	}, [openBrief, openVehicleDialog]);

	const vehicleDetailStyles = useMemo(() => ({
		headerText: {
			fontSize: 18,
			color: theme.text1,
		},
		detailView: {
			marginTop: 4,
			marginBottom: 4,
		},
		leftItemWidth: {
			minWidth: '47%',
		},
		secondButton: {
			flex: 1,
			marginLeft: 10,
		},
	}), [theme.text1]);

	if (isLoading) {
		return <Spinner />;
	}

	const refreshControl = (
		<RefreshControl
			refreshing={isRefreshing}
			onRefresh={refreshPage}
		/>
	);

	const header = (
		<DriverMyDayHeader
			user={user}
			tenant={tenant}
			trainingContext={trainingContext}
			isTraining={isTraining}
			serviceRun={serviceRun}
			clockStatus={clockStatus}
			isClockedOutForDay={isClockedOutForDay}
			rosterShift={rosterShift}
			machineFill={machineFill}
			todayDateKey={todayDateKey}
			hasEligiblePickListItems={hasEligiblePickListItems}
			openBrief={openBrief}
			openPickListSummary={openPickListSummary}
			onViewEmployeeDay={onViewEmployeeDay}
			onShowPickingWork={onShowPickingWork}
		/>
	);

	if (!serviceRun) {
		return (
			<>
				<TrainingModeBanner
					trainingContext={trainingContext}
					isOnline={isOnline}
					onBack={onExitTraining}
					onChooseEmployee={onChooseEmployee}
				/>
				<ScrollView
					style={styles.container}
					scrollIndicatorInsets={{ right: 1 }}
					refreshControl={refreshControl}
				>
					<View style={styles.content}>
						{header}
						{
							upcomingRun && (
								<View style={styles.card}>
									<Heading heading="No run today" subHeading="Your next run" />
									<Text style={styles.subtleText}>
										{`Next run: ${formatDate(upcomingRun.planned, 'DAY_MONTHWORD_YEAR')}${upcomingRun.route?.name ? ` · ${upcomingRun.route.name}` : ''}`}
									</Text>
									<Button
										label="Open service runs"
										onPress={() => navigation.push('ServiceRunListView')}
									/>
								</View>
							)
						}
						{
							!upcomingRun && (
								<View style={styles.card}>
									<Heading heading={isTraining ? 'Run unavailable' : 'No run assigned'} />
									<Text style={styles.subtleText}>
										{isTraining
											? 'This run is no longer available for training. Return to the employee preview and choose another run.'
											: 'No service run was found from today onward. See your manager if you are expecting one.'}
									</Text>
									<Button
										label={isTraining ? 'Back to employee preview' : 'Open service runs'}
										onPress={isTraining ? onExitTraining : () => navigation.push('ServiceRunListView')}
									/>
								</View>
							)
						}
					</View>
				</ScrollView>
			</>
		);
	}

	// Accordion defaults: only the active phase starts expanded; the other
	// sections collapse to their header summary until tapped.
	const isStartListCollapsed = startListExpandedOverride !== null
		? !startListExpandedOverride
		: activePhaseKey !== 'start';
	const isStopsListCollapsed = stopsListExpandedOverride !== null
		? !stopsListExpandedOverride
		: activePhaseKey !== 'stops';
	const isEndListCollapsed = endListExpandedOverride !== null
		? !endListExpandedOverride
		: activePhaseKey !== 'end';
	// The day's fresh food list matters while preparing; each stop card repeats its own part on the run.
	const isFreshFoodCollapsed = freshFoodExpandedOverride !== null
		? !freshFoodExpandedOverride
		: activePhaseKey !== 'start';

	const stopsAsSteps = sortedDockets.map(docket => ({
		status: isDocketDone(docket) ? MY_DAY_STEP_STATUS.DONE : MY_DAY_STEP_STATUS.TODO,
	}));

	return (
		<>
			<TrainingModeBanner
				trainingContext={trainingContext}
				isOnline={isOnline}
				onBack={onExitTraining}
				onChooseEmployee={onChooseEmployee}
			/>
			<ScrollView
				ref={scrollViewRef}
				style={styles.container}
				scrollIndicatorInsets={{ right: 1 }}
				refreshControl={refreshControl}
			>
				<View style={styles.content}>
					{header}
					{
						todaysRuns.length > 1 && (
							<View style={styles.runChipsRow}>
								{
									todaysRuns.map(run => (
										<TouchableOpacity
											key={run.id}
											style={[styles.runChip, run.id === serviceRun.id && styles.runChipActive]}
											activeOpacity={0.86}
											onPress={() => setActiveRunId(run.id)}
										>
											<Text style={[styles.runChipText, run.id === serviceRun.id && styles.runChipTextActive]}>
												{run.route?.name || formatDate(run.planned, 'HOUR_MINUTE_AMPM')}
											</Text>
										</TouchableOpacity>
									))
								}
							</View>
						)
					}
					<DriverMyDayFocusCard
						day={day}
						actions={actions}
						user={user}
						trainingContext={trainingContext}
						focusKey={focusKey}
						isFocusOverridden={isFocusOverridden}
						clearFocusOverride={clearFocusOverride}
						openBrief={openBrief}
						openVehicleDialog={openVehicleDialog}
						openNextStopDialog={openNextStopDialog}
						onExitTraining={onExitTraining}
					/>
					{
						freshFoodStops.length > 0 && (
							<PhaseSection
								title="Fresh food"
								steps={[]}
								collapsible={true}
								collapsed={isFreshFoodCollapsed}
								onToggleCollapsed={() => setFreshFoodExpandedOverride(isFreshFoodCollapsed)}
							>
								<FreshFoodList
									stops={freshFoodStops}
									onOpenStop={openFreshFoodStopPickList}
									canOpenStop={canOpenFreshFoodStop}
								/>
							</PhaseSection>
						)
					}
					<PhaseSection
						title="Start of day"
						steps={startOfDaySteps}
						collapsible={true}
						collapsed={isStartListCollapsed}
						onToggleCollapsed={() => setStartListExpandedOverride(isStartListCollapsed)}
					>
						{
							visibleStartSteps.map(step => (
								<StepRow
									key={step.key}
									step={step}
									onPress={() => onStepRowPress(step)}
									active={step.key === focusKey}
								/>
							))
						}
						{
							!visibleStartSteps.length && (
								<Text style={styles.subtleText}>Nothing to prepare for this run.</Text>
							)
						}
					</PhaseSection>
					{
						showStopsSection && sortedDockets.length > 0 && (
							<PhaseSection
								title="On the run"
								steps={stopsAsSteps}
								collapsible={true}
								collapsed={isStopsListCollapsed}
								onToggleCollapsed={() => setStopsListExpandedOverride(isStopsListCollapsed)}
							>
								<StopsList
									sortedDockets={sortedDockets}
									currentDocketId={runStarted ? activeDocket?.id : nextIncompleteDocket?.id}
									isDocketDone={isDocketDone}
									onSelectStop={runStarted ? selectStopInMyDay : undefined}
									onOpenStopDetails={!runStarted ? openStopDetails : undefined}
								/>
							</PhaseSection>
						)
					}
					{
						showEndOfDaySection && (
							<PhaseSection
								title="End of day"
								steps={endOfDaySteps}
								collapsible={true}
								collapsed={isEndListCollapsed}
								onToggleCollapsed={() => setEndListExpandedOverride(isEndListCollapsed)}
							>
								{
									visibleEndSteps.map(step => (
										<StepRow
											key={step.key}
											step={step}
											onPress={() => onStepRowPress(step)}
											active={step.key === focusKey}
										/>
									))
								}
							</PhaseSection>
						)
					}
				</View>
			</ScrollView>
			<ServiceRunBrief
				serviceRun={serviceRun}
				hideButton={true}
				isOpen={isBriefOpen}
				setIsOpen={setIsBriefOpen}
				requireAcknowledgement={Boolean(tenantSettings?.myDayRequireBriefAcknowledgement)}
				onAcknowledged={refetchRuns}
				disablePermanentDismiss={isTraining}
				disableActions={trainingOffline}
				adminTraining={isTraining}
			/>
			<Dialog
				isOpen={isVehicleDialogOpen}
				onClose={() => setIsVehicleDialogOpen(false)}
			>
				<View>
					<ServiceRunVehicleDetail
						servicerun={serviceRun}
						styles={vehicleDetailStyles}
						showConfirmButton={true}
						setShowDialogOpen={setIsVehicleDialogOpen}
						onUpdated={onVehicleUpdated}
						adminTraining={isTraining}
						disableActions={trainingOffline}
					/>
				</View>
			</Dialog>
			<NextStopReasonDialog
				isOpen={isNextStopDialogOpen}
				setIsOpen={setIsNextStopDialogOpen}
				stops={reorderableStops}
				onSubmit={submitNextStops}
				loading={isMakingNextStop}
			/>
			<ManualRestockDialog
				isOpen={manualRestock.isOpen}
				setIsOpen={manualRestock.setIsOpen}
				onClick={manualRestock.submit}
				values={manualRestock.values}
				changeFormValue={manualRestock.changeFormValue}
			/>
		</>
	);
};

const DriverMyDayViewWithMediaPicker = withMediaPicker(DriverMyDayView);

const DriverMyDayViewScreen = props => (
	<DriverMyDayViewWithMediaPicker
		quickMediaCapture={true}
		collectCapturedMediaBatch={true}
		{...props}
	/>
);

export default DriverMyDayViewScreen;
